import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { Primitive } from 'type-fest';

import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';
import { MISSING_ORG_NAME } from '../Steps/missingOrgName';
import { analytics } from '../utils/external-libs/segment';
import mapKeyToSnakeCase from '../utils/snakeCaseKeys';

import { useOrgData } from './useOrgData';

const getDefaultWindowParams = () => ({
  path: window.location.pathname,
  referrer: document.referrer,
  title: document.title,
});

type TrackButtonClickedProps = {
  buttonName: string;
  [key: string]: unknown;
};

const metaData = {
  context: { screen: { height: window.screen.height, width: window.screen.width } },
};

export const trackEvent = (eventName: string, params?: Record<string, Primitive>) =>
  analytics.track(eventName, {
    ...getDefaultWindowParams(),
    ...params,
  });

export const trackButtonClicked = ({ buttonName, ...restCustomMetadata }: TrackButtonClickedProps) => {
  const metadata = {
    button_name: buttonName,
    url: window.location.href,
    ...mapKeyToSnakeCase(restCustomMetadata || {}),
  };

  trackEvent('Click', metadata);
};

export const trackPage = (page: string, params?: Record<string, Primitive>) =>
  analytics.page(
    page,
    {
      ...getDefaultWindowParams(),
      ...params,
    },
    metaData
  );

export const useAnalyticsHomeownerParams = (tokenFromProps?: string) => {
  const { state } = useOnlineBookingReducer();
  const { token, initParams, contactDetails } = state;
  const { orgName } = useParams<{ orgName?: string }>();

  const { data: orgData, isFetched } = useOrgData({ token: tokenFromProps ?? token });

  const orgEmail = orgData?.email;
  const orgNameWithFallback = orgData?.name ?? orgName ?? MISSING_ORG_NAME;
  const customerUuid = initParams?.source === 'customer_portal' ? initParams?.customerUuid : '';
  const customerEmail =
    initParams?.source === 'customer_portal' ? initParams?.contactDetails?.email : contactDetails?.values?.email ?? '';

  return useMemo(() => {
    if (!isFetched) {
      return {};
    }

    return {
      org_email: orgEmail,
      org_name: orgNameWithFallback,
      email: customerEmail,
      unit_uuid: customerUuid,
    };
  }, [customerEmail, customerUuid, isFetched, orgEmail, orgNameWithFallback]);
};
